import { FC, ReactNode } from 'react';
import { Col, Popover, Row, Space } from 'antd';
import { convertMinsToHrsMins, formatTimeHM, renderTime } from 'utils/string.utils';
import ProgressBarLite from 'components/ProgressBarDouble/ProgressBarLite';
import styles from './index.module.less';

interface BudgetBarItem {
  value: number;
  color: string;
  percentage: number;
}

interface Budget {
  total: number;
  available: number;
  estimated: number;
  spent: number;
  bar: { available: BudgetBarItem; estimated: BudgetBarItem; spent: BudgetBarItem };
  summary?: { total_credits: number; total_timelogs: number; total_expired: number };
}

interface TimeReportProgressProps {
  budget?: Budget;
  report?: { total_credits?: number; total_timelogs?: number };
  infoSameLine?: 'md';
  reloadButton?: ReactNode;
  direction?: 'row' | 'column';
}

const EMPTY_INDICATOR = { value: 0, color: '#e0e0e0', percentage: 0 };

const TimeReportProgress: FC<TimeReportProgressProps> = ({ budget, report, reloadButton, direction = 'row' }) => {
  const barAvailable = budget?.bar?.available ?? EMPTY_INDICATOR;
  const barEstimated = budget?.bar?.estimated ?? EMPTY_INDICATOR;
  const barSpent = budget?.bar?.spent ?? EMPTY_INDICATOR;
  const available = barAvailable.value || budget?.available;

  const totalCredits = budget?.summary?.total_credits ?? 0;
  const totalTimelogs = budget?.summary?.total_timelogs ?? 0;
  const totalExpired = budget?.summary?.total_expired ?? 0;
  const balance = totalCredits - totalTimelogs - totalExpired;

  const budgetInfo = (
    <Row className="primary" gutter={[8, 2]} justify="space-between" style={{ flexDirection: 'column', position: 'relative' }}>
      <Col>
        Refil: <span className={totalCredits < 0 ? 'danger' : undefined}>{renderTime(convertMinsToHrsMins(totalCredits))}</span>
      </Col>

      <Col>
        Timelog:{' '}
        <span className={totalTimelogs < 0 ? 'danger' : undefined}>{renderTime(convertMinsToHrsMins(totalTimelogs))}</span>
      </Col>

      <Col>
        Expired:{' '}
        <span className={totalExpired < 0 ? 'danger' : undefined}>{renderTime(convertMinsToHrsMins(totalExpired))}</span>
      </Col>

      <Col>
        Balance: <span className={balance < 0 ? 'danger' : undefined}>{renderTime(convertMinsToHrsMins(balance))}</span>
      </Col>
    </Row>
  );

  return (
    <>
      {direction === 'row' ? (
        <Space className="w-full" direction="vertical" size={2} style={{ marginTop: 4 }}>
          <Row gutter={12} className="primary" justify="space-between">
            <Popover
              content={budgetInfo}
              title={
                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                  <Col>Budget Info</Col>
                  {reloadButton && <Col>{reloadButton}</Col>}
                </Row>
              }
            >
              <Col style={{ cursor: 'pointer' }}>Available</Col>
            </Popover>

            <Col style={{ fontWeight: 'bold' }}>
              <span className={barAvailable.value < 0 ? 'danger' : undefined}>
                {convertMinsToHrsMins(barAvailable.value)}
              </span>
              <span> / {convertMinsToHrsMins(budget?.total ?? 0)} h</span>
            </Col>
          </Row>

          <ProgressBarLite
            budget={budget?.total ?? 0}
            available={barAvailable}
            estimated={barEstimated}
            spent={barSpent}
          />
        </Space>
      ) : (
        <div className={styles.wrapper}>
          <div className={styles.info}>
            <div className={styles.block}>
              <span className={styles.label}>Time budget</span>
              <span className={styles.value}>{formatTimeHM(budget?.total ?? 0)}</span>
            </div>

            <Popover
              content={budgetInfo}
              placement="left"
              title={
                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                  <Col>Budget info</Col>
                  {reloadButton && <Col>{reloadButton}</Col>}
                </Row>
              }
            >
              <div className={styles.block} style={{ cursor: 'pointer' }}>
                <span className={styles.label}>Time left</span>
                <span className={styles.value} style={Number(available ?? 0) < 0 ? { color: 'red' } : {}}>
                  {formatTimeHM(Number(available ?? 0))}
                </span>
              </div>
            </Popover>
          </div>

          <ProgressBarLite
            direction={direction}
            budget={budget?.total ?? 0}
            available={barAvailable}
            estimated={barEstimated}
            spent={barSpent}
          />
        </div>
      )}
    </>
  );
};

export default TimeReportProgress;
